import { EditableInfoPage, SubmitProps } from '@/components/editable-info-page.js';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { LoadingPage } from '@/components/loading-page.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { getAssessmentContents, getAssessments, updateAssessmentContent } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { validateRouteRole } from '@/lib/routing.js';
import { DeleteAssessmentContent } from '@/routes/_layout/lessons/assessment-contents/-components/delete-assessment-content.js';
import { distinct, isAirAdmin } from '@airreading/common';
import {
  AssessmentFormat,
  StudentYear,
  StudentYearsInOrder,
  studentYearOption,
  UpdateAssessmentContentOptions,
} from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { Suspense } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/lessons/assessment-contents/$assessmentContentId')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: Page,
});

const editAssessmentContentFormSchema = z.object({
  name: z.string().trim().min(1, { message: 'Content name is required' }),
  words: z.string(),
  readingPassage: z.string(),
  requireStudentGrades: z.array(z.nativeEnum(StudentYear)),
  maxErrorsAllowed: z.coerce
    .number({ message: 'Please enter a valid number.' })
    .int({ message: 'Please enter an integer.' }),
  internalOrder: z.coerce
    .number({ message: 'Please enter a valid number.' })
    .int({ message: 'Please enter an integer.' }),
});

type EditAssessmentContentForm = z.infer<typeof editAssessmentContentFormSchema>;

function Page() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <AssessmentContentDetail />
    </Suspense>
  );
}

function AssessmentContentDetail() {
  const { assessmentContentId } = Route.useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: assessmentContents } = useSuspenseQuery(getAssessmentContents());
  const { data: assessments } = useSuspenseQuery(getAssessments());
  const { mutateAsync } = useMutation({
    mutationFn: updateAssessmentContent,
  });

  const data = assessmentContents.find((content) => content.id === assessmentContentId);
  if (!data) {
    throw notFound();
  }

  const isDiagnosticWordList = data.format === AssessmentFormat.diagnostic_word_list;
  const parentAssessment = assessments.find((assessment) =>
    assessment.contents.some((content) => content.id === data.id),
  );

  const defaultValues: DefaultValues<EditAssessmentContentForm> = {
    name: data.name,
    words: data.words.join(', '),
    readingPassage: data.readingPassage ?? '',
    requireStudentGrades: data.requireStudentGrades ?? [],
    maxErrorsAllowed: data.maxErrorsAllowed,
    internalOrder: data.internalOrder,
  };

  const onSubmit = ({ formData, disableEditing }: SubmitProps<EditAssessmentContentForm>) => {
    const postBody: UpdateAssessmentContentOptions = {
      name: formData.name,
      format: data.format,
      words: formData.words
        .split(',')
        .map((part) => part.trim())
        .filter((token) => token.length > 0)
        .filter(distinct()),
      readingPassage: formData.readingPassage === '' ? null : formData.readingPassage,
      requireStudentGrades: formData.requireStudentGrades,
      maxErrorsAllowed: isDiagnosticWordList ? formData.maxErrorsAllowed : data.maxErrorsAllowed,
      internalOrder: isDiagnosticWordList ? formData.internalOrder : data.internalOrder,
    };

    return mutateAsync({ id: data.id, data: postBody })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: getAssessmentContents.queryKeyFn() });
        queryClient.invalidateQueries({ queryKey: getAssessments.queryKeyFn() });
        toast({
          title: 'Assessment content updated successfully',
        });
        disableEditing();
      })
      .catch((error: unknown) => {
        toast({
          title: 'Assessment content update failed',
          description: airServerErrorFrom(error)?.userFacingMessage,
        });
      });
  };

  return (
    <EditableInfoPage
      headerText={data.name}
      formSchema={editAssessmentContentFormSchema}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      headerActions={
        <DeleteAssessmentContent
          assessmentContent={data}
          trigger={
            <Button
              variant="outline"
              data-testid="assessment-content-page-button-delete"
              aria-label={`Delete ${data.name}`}
            >
              Delete
            </Button>
          }
          onDeleted={() => navigate({ to: '/lessons/assessment-contents' })}
        />
      }
      breadcrumbs={{
        linkItems: [{ label: 'Assessment contents', link: { to: '/lessons/assessment-contents' } }],
        currentPage: data.name,
      }}
    >
      {({ form, editing }) => (
        <InfoCard headerText="Assessment content details">
          <InfoKeyValue
            label="Name"
            value={data.name}
            edit={{
              editing,
              editModeComponent: (
                <FormInputField form={form} name="name" label="Name" inputProps={{ placeholder: 'Content name' }} />
              ),
            }}
          />
          <InfoKeyValue label="Format" value={data.format} />
          <InfoKeyValue
            label="Word list"
            value={data.words.join(', ')}
            edit={{
              editing,
              editModeComponent: (
                <FormTextArea
                  form={form}
                  name="words"
                  label="Word list"
                  textAreaProps={{ placeholder: 'Comma-separated list of words, e.g. cat, dog, fish' }}
                />
              ),
            }}
          />
          <InfoKeyValue
            label="Reading passage"
            value={
              data.readingPassage ? (
                <div className="max-w-md whitespace-pre-wrap break-words">{data.readingPassage}</div>
              ) : (
                ''
              )
            }
            edit={{
              editing,
              editModeComponent: (
                <FormTextArea
                  form={form}
                  name="readingPassage"
                  label="Reading passage"
                  textAreaProps={{ className: 'whitespace-pre-wrap', placeholder: 'Reading passage' }}
                />
              ),
            }}
          />
          <InfoKeyValue
            label="Require student grades"
            value={(data.requireStudentGrades ?? []).map(studentYearOption).join(', ')}
            edit={{
              editing,
              editModeComponent: (
                <FormCheckboxGroup
                  label="Require student grades"
                  form={form}
                  name="requireStudentGrades"
                  options={StudentYearsInOrder}
                  optionLabel={studentYearOption}
                  horizontal
                />
              ),
            }}
          />
          <InfoKeyValue label="Assessment" value={parentAssessment?.name ?? ''} />
          {isDiagnosticWordList && (
            <>
              <InfoKeyValue
                label="Max errors allowed"
                value={data.maxErrorsAllowed.toString()}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="maxErrorsAllowed"
                      label="Max errors allowed"
                      inputProps={{ type: 'number', step: 1 }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Internal order"
                value={data.internalOrder.toString()}
                labelTooltip="The order of this content among others in the same assessment. Smaller numbers come first."
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="internalOrder"
                      label="Internal order"
                      inputProps={{ type: 'number', step: 1 }}
                    />
                  ),
                }}
              />
            </>
          )}
        </InfoCard>
      )}
    </EditableInfoPage>
  );
}
